<script lang="ts">
import { computed, defineComponent, inject, onMounted, reactive, ref, watch } from "vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Vorschlagsliste from "@/components/Vorschlagsliste/Vorschlagsliste.vue";
import formValidierung from "@/businesslogic/formValidierung";
import AdressenDatenService from "@/businesslogic/services/AdressenDatenService";
import Modal from "@/components/Modal.vue";
import { Adresse } from "@/model/Adresse";
import Logger from "js-logger";
import { v4 as uuidv4 } from "uuid";
import { inputChangeHandler } from "@/businesslogic/utils/events";
import InfoBox from "@/components/grid/InfoBox.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Adresse",
  methods: { inputChangeHandler },
  components: {
    InfoBox,
    Vorschlagsliste,
    Zelle,
    TitelZelle,
    Zeile,
    ValidierungsFehler,
    Modal,
  },
  props: {
    fehler: {
      type: Object,
    },
    required: {
      type: Boolean,
      default: true,
    },
    plz: String,
    ort: String,
    strasse: String,
    hausnummer: String,
    zuPruefen: {
      type: Boolean,
      default: false,
    },
    zeigeFehler: {
      type: Boolean,
      default: true,
    },
    feldNamen: Object,
    placeholder: {
      type: Boolean,
      default: true,
    },
    deaktivierteFelder: {
      type: Array,
      default: () => [],
    },
    sichtbareFelder: {
      type: Array,
      default: () => ["plz", "ort", "hausnummer", "strasse"],
    },
    clearOrtOnPlzFocus: {
      type: Boolean,
      default: false,
    },
    sessionMarker: {
      type: String,
      default: "",
    },
    zeileClass: {
      type: String,
      required: false,
    },
    titel: {
      type: Object,
    },
    plzFormat: {
      type: Object,
    },
    ortFormat: {
      type: Object,
    },
    strasseFormat: {
      type: Object,
    },
    hausnummerFormat: {
      type: Object,
    },
    zeigeInfo: {
      type: Boolean,
      default: false,
    },
    infoText: {
      type: String,
    },
  },
  inheritAttrs: false,

  setup(props, context) {
    const VORSCHLAG_VERZOEGERUNG = 500;

    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const adresse = reactive({
      strasse: props.strasse,
      hausnummer: props.hausnummer,
      plz: props.plz,
      ort: props.ort,
      zuPruefen: props.zuPruefen,
    });

    const feldNamenNofill = {
      strasse: "s-" + Math.floor(Math.random() * 1000000000),
      hausnummer: "h-" + Math.floor(Math.random() * 1000000000),
      plz: "p-" + Math.floor(Math.random() * 1000000000),
      ort: "o-" + Math.floor(Math.random() * 1000000000),
    };

    const iFehler = props.fehler ? ref(props.fehler) : ref({} as any);

    const plzInput = ref<HTMLInputElement>();
    const strasseInput = ref<HTMLInputElement>();
    const ortInput = ref<HTMLInputElement>();
    const hausnummerInput = ref<HTMLInputElement>();

    const adressVorschlaege = ref([]);
    const selectedVorschlag = ref<number>(0);
    const zeigeVorschlaegePlz = ref(false);
    const zeigeVorschlaegeOrt = ref(false);
    const zeigeVorschlaegeStrasse = ref(false);
    const eingabeValide = ref(false);
    const plzValide = ref(false);
    const ortValide = ref(false);
    const strasseValide = ref(false);
    const hausnrValide = ref(false);
    const adresseValide = ref(false);
    const validatorAdresse = ref([
      {
        strasse: props.strasse,
        hausnummer: props.hausnummer,
        plz: props.plz,
        ort: props.ort,
      },
    ]);
    const validatorAdresseVerwenden = ref(-1);
    const zeigeSpinner: any = inject("zeigeSpinner");

    const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();

    watch(
      () => props.fehler,
      (werteNeu) => {
        iFehler.value = werteNeu;
      },
    );

    watch(
      () => props.strasse,
      (werteNeu) => {
        adresse.strasse = werteNeu;
      },
    );

    watch(
      () => props.hausnummer,
      (werteNeu) => {
        adresse.hausnummer = werteNeu;
      },
    );

    watch(
      () => props.plz,
      (werteNeu) => {
        adresse.plz = werteNeu;
      },
    );

    watch(
      () => props.ort,
      (werteNeu) => {
        adresse.ort = werteNeu;
      },
    );

    watch(
      () => props.zuPruefen,
      (werteNeu) => {
        adresse.zuPruefen = werteNeu;
      },
    );

    const decVorschlag = () => {
      selectedVorschlag.value--;
      if (selectedVorschlag.value < 0) {
        selectedVorschlag.value = 0;
      }
    };

    const incVorschlag = () => {
      selectedVorschlag.value++;
      if (selectedVorschlag.value > adressVorschlaege.value.length) {
        selectedVorschlag.value = 0;
      }
    };

    const akzeptiereVorschlag = (value: any) => {
      if (value && value.index) {
        selectedVorschlag.value = value.index;
      }

      if (adressVorschlaege.value && adressVorschlaege.value.length > 0) {
        const vorschlag: Adresse = adressVorschlaege.value[selectedVorschlag.value];

        if (vorschlag.strasse) {
          context.emit("update:strasse", vorschlag.strasse);
          hausnummerInput.value?.focus();
        }
        if (vorschlag.hausnummer) {
          context.emit("update:hausnummer", vorschlag.hausnummer);
        }
        if (vorschlag.plz) {
          context.emit("update:plz", vorschlag.plz);
          ortInput.value?.focus();
        }
        if (vorschlag.ort) {
          context.emit("update:ort", vorschlag.ort);
          strasseInput.value?.focus();
        }
        verbergeVorschlaege();
      }
    };

    let updateStrasseFlag: any;

    const updateStrasse = (value: string, vorschlaegeZeigen: boolean) => {
      clearUpdateStrasse();
      updateStrasseFlag = setTimeout(_updateStrasse, VORSCHLAG_VERZOEGERUNG, value, vorschlaegeZeigen);
    };

    const clearUpdateStrasse = () => {
      if (updateStrasseFlag) {
        clearTimeout(updateStrasseFlag);
      }
    };

    const _updateStrasse = (value: string, vorschlaegeZeigen: boolean) => {
      adresse.zuPruefen = true;
      adresse.strasse = value;
      context.emit("update:strasse", value);
      aktualisiereVorschlaege("/streetAutocomplete").then(() => {
        iFehler.value[props.feldNamen?.strasse] = "";
        zeigeVorschlaegeStrasse.value = vorschlaegeZeigen && adressVorschlaege.value?.length > 0;
      });
    };

    const updateHausnummer = (value: string) => {
      adresse.zuPruefen = true;
      adresse.hausnummer = value;
      context.emit("update:hausnummer", value);
    };

    let updatePlzFlag: any;

    const updatePlz = (value: string, vorschlaegeZeigen: boolean) => {
      clearUpdatePlz();
      updatePlzFlag = setTimeout(_updatePlz, VORSCHLAG_VERZOEGERUNG, value, vorschlaegeZeigen);
    };

    const clearUpdatePlz = () => {
      if (updatePlzFlag) {
        clearTimeout(updatePlzFlag);
      }
    };

    const _updatePlz = (value: string, vorschlaegeZeigen: boolean) => {
      adresse.zuPruefen = true;
      adresse.plz = value;
      context.emit("update:plz", value);
      aktualisiereVorschlaege("/postCodeAutocomplete")
        .then(() => {
          iFehler.value[props.feldNamen?.plz] = "";
          zeigeVorschlaegePlz.value = vorschlaegeZeigen && adressVorschlaege.value?.length > 0;
        })
        .catch((fehler) => {
          Logger.debug("_updatePlz schlug fehl:", fehler);
        });
    };

    let updateOrtFlag: any;

    const updateOrt = (value: string, vorschlaegeZeigen: boolean) => {
      clearUpdateOrt();
      updateOrtFlag = setTimeout(_updateOrt, VORSCHLAG_VERZOEGERUNG, value, vorschlaegeZeigen);
    };

    const clearUpdateOrt = () => {
      if (updateOrtFlag) {
        clearTimeout(updateOrtFlag);
      }
    };

    const loescheOrt = () => {
      if (props.clearOrtOnPlzFocus === true) {
        adresse.zuPruefen = true;
        adresse.ort = "";
        context.emit("update:ort", "");
      }
    };

    const _updateOrt = (value: string, vorschlaegeZeigen: boolean) => {
      adresse.zuPruefen = true;
      adresse.ort = value;
      context.emit("update:ort", value);
      aktualisiereVorschlaege("/cityNameAutocomplete").then(() => {
        iFehler.value[props.feldNamen?.ort] = "";
        zeigeVorschlaegeOrt.value = vorschlaegeZeigen && adressVorschlaege.value?.length > 0;
      });
    };

    const aktualisiereVorschlaege = (url: string) => {
      zeigeSpinner.value = true;
      if (!piniaStore.state.sessionId) {
        setzeSessionId();
      }
      return AdressenDatenService.vorschlagen(
        piniaStore.state.sessionId + props.sessionMarker,
        adresse.plz,
        adresse.ort,
        adresse.strasse,
        url,
      )
        .then((res) => {
          adressVorschlaege.value = res.vorschlaege;
          eingabeValide.value = res.status && res.status[0] !== "A3000";
          zeigeSpinner.value = false;
          selectedVorschlag.value = 0;
          return res;
        })
        .catch((reason) => {
          Logger.debug("aktualisiereVorschlaege Fehler", reason);
        })
        .finally(() => {
          zeigeSpinner.value = false;
        });
    };

    const verbergeVorschlaege = () => {
      zeigeVorschlaegePlz.value = false;
      zeigeVorschlaegeOrt.value = false;
      zeigeVorschlaegeStrasse.value = false;
      selectedVorschlag.value = 0;
    };

    const pruefen = async (
      pruefungMussErfolgtSein: boolean,
      verlaesslichCallback?: { (verlaesslich: boolean): void },
    ): Promise<boolean> => {
      if (adresse.zuPruefen) {
        if (pruefungMussErfolgtSein) {
          await adresseValidator()
            .then((res) => {
              if (verlaesslichCallback) {
                verlaesslichCallback(res.status !== 200 || res.data.verlaesslich);
              }
              validiereForm("", true).then(() => (iFehler.value = getFormFehler()));
            })
            .catch(() => {
              if (verlaesslichCallback) {
                verlaesslichCallback(false);
              }
            });
        } else {
          await validiereForm("", true);
          iFehler.value = getFormFehler();
        }
      } else {
        await validiereForm("", true);
        iFehler.value = getFormFehler();
      }

      Logger.debug(
        "Adresse pruefen",
        props.sessionMarker,
        "zu pruefen: " + adresse.zuPruefen,
        "Fehlerzahl: " + getFormFehlerAnzahl(),
        "Adresse valide: " + adresseValide.value,
        "Formfehler: ",
        getFormFehler(),
      );

      return !adresse.zuPruefen || (getFormFehlerAnzahl() == 0 && adresseValide.value);
    };

    function adresseValidator() {
      zeigeSpinner.value = true;
      if (!piniaStore.state.sessionId) {
        setzeSessionId();
      }
      return AdressenDatenService.pruefen(
        piniaStore.state.sessionId + props.sessionMarker,
        adresse.plz,
        adresse.ort,
        adresse.strasse,
        adresse.hausnummer,
      )
        .then((res) => {
          if (res.status == 200) {
            plzValide.value = res.data.plzKorrekt;
            ortValide.value = res.data.ortKorrekt;
            strasseValide.value = res.data.strasseKorrekt;
            hausnrValide.value = res.data.hausnummerKorrekt;
            adresseValide.value = res.data.addresseKorrekt;

            resetValidatorAdresse();

            if (!adresseValide.value) {
              if (res.data.vorschlaege.length > 0) {
                validatorAdresse.value = res.data.vorschlaege;
                wechsleSichtbarkeit("modal-verbesserungsVorschlag-" + props.sessionMarker);
              } else {
                wechsleSichtbarkeit("modal-manuelleAdresse-" + props.sessionMarker);
              }
            }
          }
          return res;
        })
        .finally(() => {
          zeigeSpinner.value = false;
        });
    }

    const resetValidatorAdresse = () => {
      validatorAdresse.value = [];
      validatorAdresseVerwenden.value = -1;
    };

    const setzeSessionId = () => {
      Logger.debug("sessionId ist beim initialisieren nicht gesetzt worden");
      piniaStore.state.sessionId = uuidv4();
      Logger.debug("sessionId nachträglich gesetzt", piniaStore.state.sessionId);
    };

    onMounted(() => {
      document.addEventListener("click", verbergeVorschlaege);
    });

    function emitKompletteAdresse() {
      context.emit("update:plz", adresse.plz);
      context.emit("update:ort", adresse.ort);
      context.emit("update:strasse", adresse.strasse);
      context.emit("update:hausnummer", adresse.hausnummer);
    }

    const ubernehmeValidatorAdresse = () => {
      wechsleSichtbarkeit("modal-verbesserungsVorschlag-" + props.sessionMarker);
      if (validatorAdresseVerwenden.value >= 0) {
        adresse.plz = validatorAdresse.value[validatorAdresseVerwenden.value].plz;
        adresse.ort = validatorAdresse.value[validatorAdresseVerwenden.value].ort;
        adresse.strasse = validatorAdresse.value[validatorAdresseVerwenden.value].strasse;
        adresse.hausnummer = validatorAdresse.value[validatorAdresseVerwenden.value].hausnummer;

        context.emit("update:plz", adresse.plz);
        context.emit("update:ort", adresse.ort);
        context.emit("update:strasse", adresse.strasse);
        context.emit("update:hausnummer", adresse.hausnummer);
      }
      adresse.zuPruefen = false;
    };

    const ubernehmeManuelleAdresse = () => {
      wechsleSichtbarkeit("modal-manuelleAdresse-" + props.sessionMarker);
      emitKompletteAdresse();
      adresse.zuPruefen = false;
    };

    const schliesseManuelleAdresse = () => {
      wechsleSichtbarkeit("modal-manuelleAdresse-" + props.sessionMarker);
      plzInput.value?.focus();
    };

    return {
      props,
      updatePlz,
      clearUpdatePlz,
      updateOrt,
      clearUpdateOrt,
      updateStrasse,
      clearUpdateStrasse,
      updateHausnummer,
      placeholderPlz: computed(() => (props.placeholder ? "PLZ" : "")),
      placeholderOrt: computed(() => (props.placeholder ? "Ort" : "")),
      placeholderStrasse: computed(() => (props.placeholder ? "Straße" : "")),
      placeholderHausnummer: computed(() => (props.placeholder ? "Nr." : "")),
      validatorAdresse,
      validatorAdresseVerwenden,
      ubernehmeValidatorAdresse,
      ubernehmeManuelleAdresse,
      schliesseManuelleAdresse,
      zeigeVorschlaegePlz,
      zeigeVorschlaegeOrt,
      zeigeVorschlaegeStrasse,
      adressVorschlaege,
      pruefen,
      iFehler,
      istSichtbar,
      wechsleSichtbarkeit,
      adresse,
      akzeptiereVorschlag,
      decVorschlag,
      incVorschlag,
      plzInput,
      strasseInput,
      ortInput,
      hausnummerInput,
      selectedVorschlag,
      feldNamenNofill,
      loescheOrt,
    };
  },
});
</script>

<template>
  <form @submit.prevent="pruefen(false)" novalidate>
    <zeile v-bind:class="zeileClass" v-if="sichtbareFelder.includes('plz') || sichtbareFelder.includes('ort')">
      <titel-zelle
        :breite="titel?.breite"
        :ausrichtung-responsiv="titel?.ausrichtungResponsiv"
        :zeige-info="zeigeInfo"
        info-box-id="infoAdresse"
      >
        <slot name="plzOrtLabel">PLZ und Ort</slot>
      </titel-zelle>
      <zelle :breite="plzFormat?.breite ? plzFormat.breite : 3" v-if="sichtbareFelder.includes('plz')">
        <input
          autocomplete="chrome-off"
          type="text"
          :name="feldNamenNofill.plz"
          :value="adresse.plz"
          @input="updatePlz(inputChangeHandler($event), true)"
          @blur="clearUpdatePlz"
          @keydown.enter="akzeptiereVorschlag(undefined)"
          @keydown.up="decVorschlag()"
          @keydown.down="incVorschlag()"
          @keydown.tab="zeigeVorschlaegePlz = false"
          @focus="loescheOrt()"
          class="c-form-control__input"
          :data-cy="feldNamen.plz"
          :placeholder="placeholderPlz"
          minlength="5"
          maxlength="5"
          min="01001"
          max="99998"
          :disabled="deaktivierteFelder.includes('plz')"
          :required="required"
          :aria-required="required"
          aria-label="Postleitzahl"
          :data-checkplz="adresse.zuPruefen"
          data-fehlermeldung="Bitte geben Sie eine gültige Postleitzahl ein."
          ref="plzInput"
        />
        <vorschlagsliste
          :vorschlaege="adressVorschlaege"
          :anzeigen="zeigeVorschlaegePlz"
          :selected="selectedVorschlag"
          @update="akzeptiereVorschlag"
        />
        <validierungs-fehler v-if="zeigeFehler" :fehler-text="iFehler[feldNamenNofill.plz]" />
      </zelle>
      <zelle :breite="ortFormat?.breite ? ortFormat.breite : 3" v-if="sichtbareFelder.includes('ort')">
        <input
          autocomplete="chrome-off"
          type="text"
          :name="feldNamenNofill.ort"
          :value="adresse.ort"
          @input="updateOrt(inputChangeHandler($event), true)"
          @blur="clearUpdateOrt"
          @keydown.enter="akzeptiereVorschlag(undefined)"
          @keydown.up="decVorschlag()"
          @keydown.down="incVorschlag()"
          @keydown.tab="zeigeVorschlaegeOrt = false"
          class="c-form-control__input"
          :data-cy="feldNamen.ort"
          :placeholder="placeholderOrt"
          minlength="2"
          maxlength="40"
          :disabled="deaktivierteFelder.includes('ort')"
          :required="required"
          :aria-required="required"
          aria-label="Ort"
          data-fehlermeldung="Bitte geben Sie den Ort ein (max. 40 Zeichen)."
          ref="ortInput"
        />
        <vorschlagsliste
          :vorschlaege="adressVorschlaege"
          :anzeigen="zeigeVorschlaegeOrt"
          :selected="selectedVorschlag"
          @update="akzeptiereVorschlag"
        />
        <validierungs-fehler v-if="zeigeFehler" :fehler-text="iFehler[feldNamenNofill.ort]" />
      </zelle>
    </zeile>

    <zeile
      v-bind:class="zeileClass"
      v-if="sichtbareFelder.includes('strasse') || sichtbareFelder.includes('hausnummer')"
    >
      <titel-zelle :breite="titel?.breite" v-bind:ausrichtung-responsiv="titel?.ausrichtungResponsiv">
        <slot name="strasseHausnummerLabel">Straße und Hausnummer</slot>
      </titel-zelle>
      <zelle :breite="strasseFormat?.breite ? strasseFormat.breite : 3" v-if="sichtbareFelder.includes('strasse')">
        <input
          autocomplete="chrome-off"
          type="text"
          :name="feldNamenNofill.strasse"
          :value="adresse.strasse"
          @input="updateStrasse(inputChangeHandler($event), true)"
          @blur="clearUpdateStrasse"
          @keydown.enter="akzeptiereVorschlag(undefined)"
          @keydown.up="decVorschlag()"
          @keydown.down="incVorschlag()"
          @keydown.tab="zeigeVorschlaegeStrasse = false"
          class="c-form-control__input"
          :data-cy="feldNamen.strasse"
          :placeholder="placeholderStrasse"
          minlength="2"
          maxlength="60"
          :disabled="deaktivierteFelder.includes('strasse')"
          :required="required"
          :aria-required="required"
          aria-label="Strasse"
          data-fehlermeldung="Bitte geben Sie die Straße ein (max. 60 Zeichen)."
          ref="strasseInput"
        />
        <vorschlagsliste
          :vorschlaege="adressVorschlaege"
          :anzeigen="zeigeVorschlaegeStrasse"
          :selected="selectedVorschlag"
          @update="akzeptiereVorschlag"
        />
        <validierungs-fehler v-if="zeigeFehler" :fehler-text="iFehler[feldNamenNofill.strasse]" />
      </zelle>
      <zelle
        :breite="hausnummerFormat?.breite ? hausnummerFormat.breite : 3"
        v-if="sichtbareFelder.includes('hausnummer')"
      >
        <input
          autocomplete="chrome-off"
          type="text"
          :name="feldNamenNofill.hausnummer"
          :value="adresse.hausnummer"
          @input="updateHausnummer(inputChangeHandler($event))"
          @keydown.enter="akzeptiereVorschlag(undefined)"
          class="c-form-control__input"
          :data-cy="feldNamen.hausnummer"
          :style="hausnummerFormat?.style ? hausnummerFormat.style : 'width:25%'"
          :placeholder="placeholderHausnummer"
          minlength="1"
          maxlength="10"
          :disabled="deaktivierteFelder.includes('hausnummer')"
          :required="required"
          :aria-required="required"
          aria-label="Hausnummer"
          data-fehlermeldung="Bitte geben Sie die Hausnummer ein (max. 10 Zeichen)."
          ref="hausnummerInput"
        />
        <validierungs-fehler v-if="zeigeFehler" :fehler-text="iFehler[feldNamenNofill.hausnummer]" />
      </zelle>
    </zeile>
    <Zeile>
      <Zelle :offset="4">
        <info-box id="infoAdresse" role="tooltip" aria-describedby="infoAdresse" :sichtbarkeit="wechsleSichtbarkeit">
          {{ infoText }}
        </info-box>
      </Zelle>
    </Zeile>
  </form>
  <modal
    v-if="istSichtbar('modal-manuelleAdresse-' + sessionMarker)"
    titel="Adressvorschlag"
    :hat-ja-nein-button="true"
    schliessen-button-text="Adresse bestätigen"
    @closeModal="schliesseManuelleAdresse"
    @closeModalJa="ubernehmeManuelleAdresse"
  >
    <template #inhalt>
      Diese Adresse ist uns leider nicht bekannt:
      <p>
        <strong
          >{{ adresse.strasse }} {{ adresse.hausnummer }}<br />
          {{ adresse.plz }} {{ adresse.ort }}</strong
        >
      </p>
      Wollen Sie sie dennoch verwenden?
    </template>
  </modal>
  <modal
    v-if="istSichtbar('modal-verbesserungsVorschlag-' + sessionMarker)"
    titel="Adressvorschlag"
    :hat-schliessen-button="true"
    schliessen-button-text="Adresse wählen"
    @closeModal="ubernehmeValidatorAdresse"
  >
    <template v-slot:inhalt>
      <div style="padding-bottom: 1rem">
        <strong>Diese Adresse ist uns leider nicht bekannt</strong>, allerdings haben wir eine ähnliche Adresse
        gefunden.<br />
        <strong>Bitte wählen Sie Ihre Adresse:</strong>
      </div>

      <zeile>
        <titel-zelle class="address-vorschlag">Ursprüngliche Adresse:</titel-zelle>
        <zelle class="mkRadio">
          <input
            v-model="validatorAdresseVerwenden"
            type="radio"
            id="validatorAdresseVerwendenNein"
            name="validatorAdresseVerwenden"
            :value="-1"
            aria-label="Ursprüngliche Adresse"
            data-fehlermeldung="Bitte treffen Sie eine Auswahl"
            data-cy="validatorAdresseVerwendenNein"
          />
          <label for="validatorAdresseVerwendenNein" class="radiolabel">
            {{ adresse.strasse }} {{ adresse.hausnummer }}<br />
            {{ adresse.plz }}
            {{ adresse.ort }}
          </label>
        </zelle>
      </zeile>
      <zeile>
        <titel-zelle class="address-vorschlag">Vorgeschlagene Adresse:</titel-zelle>
        <zelle class="mkRadio">
          <div class="address-vorschlaege">
            <div v-for="(option, index) in validatorAdresse" :key="index">
              <input
                v-model="validatorAdresseVerwenden"
                type="radio"
                :id="'ungepruefteAdresseVerwenden' + index"
                name="validatorAdresseVerwenden"
                aria-label="Vorgeschlagene Adresse"
                :value="index"
                :data-cy="'ungepruefteAdresseVerwenden' + index"
              />
              <label :for="'ungepruefteAdresseVerwenden' + index" class="radiolabel">
                <div>
                  {{ option.strasse }}
                  {{ option.hausnummer }}<br />
                  {{ option.plz }} {{ option.ort }}
                </div>
              </label>
            </div>
          </div>
        </zelle>
      </zeile>
    </template>
  </modal>
</template>

<style scoped lang="scss">
.address-vorschlag {
  font-weight: bold;
}

.address-vorschlaege {
  overflow-y: auto;
  max-height: 20rem;
}

.mkRadio {
  .radiolabel {
    display: inline-flex !important;
  }
}
</style>
